import { useMemo, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/lib/auth';
import { isMasterEmail } from '@/shared/lib/navigation';
import { FullPageLoader } from '@/shared/components/full-page-loader';
import { EmailMarketingProvider, PLATFORM_EMAIL_SCOPE } from '@/features/email-marketing';
import { PLATFORM_EMAIL_TEMPLATES } from '@/mocks/platform';
import { PlatformProvider, usePlatform } from '../model/demo-context';
import { renderSurveyBlock } from '../model/survey-block';
import { VARIABLE_SAMPLES } from '../model/types';

/** O editor de template ocupa a tela inteira, como no módulo da loja. */
function isTemplateWorkspace(pathname: string) {
  return /\/plataforma\/templates\/(novo|[^/]+\/editar)$/.test(pathname);
}

/**
 * Escopo do módulo de e-mail na plataforma. Remetente e exemplos das variáveis
 * vêm da configuração: o `{{bloco_pesquisa}}` da prévia é o bloco que o master
 * montou na aba E-mails.
 */
function PlatformEmailScope({ children }: { children: ReactNode }) {
  const { config } = usePlatform();
  const scope = useMemo(() => {
    const transaction = config.emails.find((email) => email.kind === 'transaction');
    return {
      ...PLATFORM_EMAIL_SCOPE,
      sampleVariables: {
        ...VARIABLE_SAMPLES,
        bloco_pesquisa: transaction?.surveyBlock
          ? renderSurveyBlock(transaction.surveyBlock, VARIABLE_SAMPLES.link_pesquisa)
          : '',
      },
      sender: { name: config.sender.fromName, email: config.sender.fromEmail },
    };
  }, [config]);
  return (
    <EmailMarketingProvider scope={scope} initialTemplates={PLATFORM_EMAIL_TEMPLATES}>
      {children}
    </EmailMarketingProvider>
  );
}

/** `/plataforma`: só o master. O módulo de e-mail roda aqui no escopo da plataforma. */
export function PlatformLayout() {
  const { user, isLoading } = useAuth();
  const { pathname } = useLocation();
  if (isLoading) return <FullPageLoader />;
  if (!user || !isMasterEmail(user.email)) return <Navigate to="/clientes" replace />;

  return (
    <PlatformProvider>
      <PlatformEmailScope>
        {isTemplateWorkspace(pathname) ? (
          <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
            <Outlet />
          </div>
        ) : (
          <Outlet />
        )}
      </PlatformEmailScope>
    </PlatformProvider>
  );
}
